import { AnimatePresence, motion, type Variants } from "framer-motion";
import { cloneElement, forwardRef, type ForwardRefRenderFunction, type ReactElement, useMemo } from "react";
import { type AriaPopoverProps, DismissButton, FocusScope, Overlay, usePopover } from "react-aria";
import type { OverlayTriggerState } from "react-stately";

import { useDOMRef } from "../../hooks.ts";

export interface PopoverProps extends Omit<AriaPopoverProps, "popoverRef"> {
  state: OverlayTriggerState;
  children: ReactElement;
}

const PopoverRender: ForwardRefRenderFunction<HTMLDivElement, PopoverProps> = (
  { state, children, triggerRef, ...props },
  ref,
) => {
  const popoverRef = useDOMRef(ref);
  const { popoverProps, underlayProps, placement, arrowProps } = usePopover(
    { ...props, offset: props.offset ?? 3, triggerRef, popoverRef },
    state,
  );

  const transformSide = useMemo(() => {
    const rl = props.placement?.split(" ");
    if (!rl) return null;
    if (rl.includes("start") || rl.includes("left")) return "left";
    if (rl.includes("end") || rl.includes("right")) return "right";
    return "center";
  }, [props.placement]);

  const variants: Variants = {
    initial: {
      opacity: 0,
      y: placement === "top" ? 5 : -5,
      scale: 0.9,
      transformOrigin: placement === "top" ? `bottom ${transformSide ?? ""}` : `top ${transformSide ?? ""}`,
    },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, transition: { duration: 0.1 } },
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const content = cloneElement(children as ReactElement<any>, { arrowProps, placement });

  return (
    <AnimatePresence>
      {state.isOpen && (
        <Overlay>
          <div
            {...underlayProps}
            // All Dialogs/Popovers get a z-index of 100_000 by react-aria.
            style={{ position: "fixed", inset: 0, zIndex: props.isNonModal ? undefined : 100_000 }}
            data-testid="popover-overlay"
          />
          <div {...popoverProps} ref={popoverRef} data-testid="popover">
            <motion.div
              variants={variants}
              initial="initial"
              animate="animate"
              exit="exit"
              style={{ maxHeight: "inherit" }}
            >
              {props.isNonModal ? (
                <>
                  {content}
                  <DismissButton
                    onDismiss={() => {
                      state.close();
                    }}
                  />
                </>
              ) : (
                <FocusScope contain restoreFocus autoFocus>
                  <DismissButton
                    onDismiss={() => {
                      state.close();
                    }}
                  />
                  {content}
                  <DismissButton
                    onDismiss={() => {
                      state.close();
                    }}
                  />
                </FocusScope>
              )}
            </motion.div>
          </div>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

export const Popover = forwardRef(PopoverRender);
